import { createAppSlice } from "../../app/createAppSlice"
import { createEntityAdapter, isAnyOf } from '@reduxjs/toolkit'
import { startAppListening } from "../../app/listenerMiddleware"
import { changedResourceAdded } from "../changedResourcesList/changedResourcesListSlice"
import getTargetResourceRatingDictionary from "../../functions/getTargetResourceRatingDictionaryAPI"
import { selectCurrentUserApiToken } from "../currentUser/currentUserSlice"

const fetchOutcomeImportanceAPI = async (targetResources, apiToken, subject) => {
    let outcomeImportanceEntities = [];
    let ratingDictionary;
    if (targetResources?.length > 0) {
        let response = await getTargetResourceRatingDictionary(targetResources, apiToken, subject);
        if (response?.success && response.targetResourceRatingDictionary) {
            ratingDictionary = response.targetResourceRatingDictionary;
            //response includes { "success": true,
            //  "targetResourceRatingDictionary": targetResourceRatingDictionary }
            // where targetResourceRatingDictionary uses foi as key and object with
            //    {
            //        "exists": false, "resource": "", "targettitle": targettitle,
            //         "targettitleTrimmed": targettitle.replace("OutcomeDefinition: ", ""),
            //        "resourceFOI": "", "resourcetitle": "", "desirabilityCode": "", "desirabilityDisplay": "",
            //        "quantityValue": 0, "fli": resource.fli
            //    }
            if (ratingDictionary && Object.keys(ratingDictionary).length > 0) {
                for (let targetFoi in ratingDictionary) {
                    let entity = {
                        id: targetFoi,
                        exists: ratingDictionary[targetFoi].exists,
                        importanceFoi: ratingDictionary[targetFoi].resourceFOI,
                        resourceJson: ratingDictionary[targetFoi].resource,
                        importanceReference: {},
                        targettitle: ratingDictionary[targetFoi].targettitle,
                        targettitleTrimmed: ratingDictionary[targetFoi].targettitleTrimmed,
                        importanceTitle: ratingDictionary[targetFoi].resourcetitle,
                        desirabilityCode: ratingDictionary[targetFoi].desirabilityCode,
                        desirabilityDisplay: ratingDictionary[targetFoi].desirabilityDisplay,
                        percentageValue: ratingDictionary[targetFoi].quantityValue,
                        importanceFli: ratingDictionary[targetFoi].fli,
                        itemChanged: false
                    };
                    if (ratingDictionary[targetFoi].resource) {
                        entity.importanceReference = {
                            "reference": "ArtifactAssessment/" + ratingDictionary[targetFoi].resourceFOI,
                            "type": "ArtifactAssessment"
                        };
                        if (ratingDictionary[targetFoi].resourcetitle) {
                            entity.importanceReference["display"] = ratingDictionary[targetFoi].resourcetitle;
                        }
                    }
                    outcomeImportanceEntities.push(entity);
                }
            }
        } else {
            console.log("FAILURE getting ratingDictionary");
        }
    }
    return outcomeImportanceEntities;
}

//entity: {  id: targetFoi,  for EvidenceVariable
//  exists: ratingDictionary[targetFoi].exists,
// importanceFoi: ratingDictionary[targetFoi].resourceFOI,
// resourceJson: ratingDictionary[targetFoi].resource,
// importanceReference: { reference, type, display },
// targettitle: ratingDictionary[targetFoi].targettitle,
// targettitleTrimmed: ratingDictionary[targetFoi].targettitleTrimmed,
// importanceTitle: ratingDictionary[targetFoi].resourcetitle,
// desirabilityCode: ratingDictionary[targetFoi].desirabilityCode,
// desirabilityDisplay: ratingDictionary[targetFoi].desirabilityDisplay,
// percentageValue: ratingDictionary[targetFoi].quantityValue,
// importanceFli: ratingDictionary[targetFoi].fli,
//  itemChanged: false
// 
//key uses: customized Summary of Findings and Summary of Net Effect, Rate Relative Importance

const outcomeImportanceAdapter = createEntityAdapter({
    sortComparer: (b, a) => {
        if (b?.targetFoi && a?.targetFoi && b.targetFoi !== a.targetFoi) {
            return b.targetFoi.localeCompare(a.targetFoi)
        } else {
            return 0
        }
    }
})

const initialState = outcomeImportanceAdapter.getInitialState({
    status: 'idle',
    error: null
})

const outcomeImportanceSlice = createAppSlice({
    name: "outcomeImportance",
    initialState,
    reducers: create => ({
        importanceAdded: create.preparedReducer(
            (importanceBody) => {
                return {
                    payload: {
                        id: importanceBody.id,
                        exists: importanceBody.exists,
                        importanceFoi: importanceBody.importanceFoi,
                        resourceJson: importanceBody.resourceJson,
                        importanceReference: importanceBody.importanceReference,
                        targettitle: importanceBody.targettitle,
                        targettitleTrimmed: importanceBody.targettitleTrimmed,
                        importanceTitle: importanceBody.importanceTitle,
                        desirabilityCode: importanceBody.desirabilityCode,
                        desirabilityDisplay: importanceBody.desirabilityDisplay,
                        percentageValue: importanceBody.percentageValue,
                        importanceFli: importanceBody.importanceFli,
                        itemChanged: importanceBody.itemChanged
                    }
                };
            },
            outcomeImportanceAdapter.addOne
        ),
        importanceUpdated: create.reducer((state, action) => {
            const { id, exists, importanceFoi, resourceJson, importanceReference, targettitle,
                targettitleTrimmed, importanceTitle, desirabilityCode,
                desirabilityDisplay, percentageValue, importanceFli, itemChanged } = action.payload;
            outcomeImportanceAdapter.updateOne(state,
                {
                    id, changes: {
                        exists, importanceFoi, resourceJson, importanceReference, targettitle,
                        targettitleTrimmed, importanceTitle, desirabilityCode,
                        desirabilityDisplay, percentageValue, importanceFli, itemChanged
                    }
                })
        }),
        fetchOutcomeImportance: create.asyncThunk(
            async (body, { getState }) => {
                const state = getState();
                const apiToken = selectCurrentUserApiToken(state);
                const resourceEntities = await fetchOutcomeImportanceAPI(body.targetResources, apiToken, body.subject);
                return resourceEntities
            },
            {
                /*
                 options: {
                     condition(arg, thunkApi) {
                         const { outcomeImportance } = thunkApi.getState()
                         if (outcomeImportance.status !== 'idle') {
                             return false
                         }
                     }
                 },
                 */
                pending: state => {
                    state.status = "loading"
                },
                fulfilled: (state, action) => {
                    state.status = "succeeded"
                    outcomeImportanceAdapter.setAll(state, action.payload)
                },
                rejected: (state, action) => {
                    state.status = "failed"
                    state.error = action.error.message ?? "Unknown Error"
                },
            },
        ),
    }),
    selectors: {
        selectOutcomeImportanceStatus: outcomeImportance => outcomeImportance.status,
        selectOutcomeImportanceError: outcomeImportance => outcomeImportance.error
    },
})

export const { importanceAdded, importanceUpdated, fetchOutcomeImportance } = outcomeImportanceSlice.actions

export const { selectOutcomeImportanceStatus, selectOutcomeImportanceError } = outcomeImportanceSlice.selectors

export const {
    selectAll: selectAllImportance,
    selectById: selectImportanceByTargetId,
} = outcomeImportanceAdapter.getSelectors(state => state.outcomeImportance)
/*
export const addOutcomeImportanceListeners = () => {

    startAppListening({
        matcher: isAnyOf(importanceAdded, importanceUpdated),
        effect: (action, listenerApi) => {
            try {
                const resourceChange = action.payload;
                listenerApi.dispatch(changedResourceAdded({ singleUpdate: resourceChange.importanceFoi }));
            } catch (err) {
                console.log("FAILURE of outcomeImportance listener: ", err)
            }
        }
    })
}
*/
export default outcomeImportanceSlice